import { useCallback, useEffect, useRef, useState } from "react";
import { archiveTeamInbox, createTeamInbox, updateTeamInbox } from "@/api/inboxAgentApi";

const DEFAULT_ICON = "ri-folder-line";

/**
 * @typedef {{ id: string, label?: string, name?: string, icon?: string, color?: string, sortOrder?: number, workspaceId?: string, workspaceName?: string, count?: number }} FolderShape
 */

/**
 * @param {{
 *   open: boolean,
 *   mode: "create" | "edit",
 *   folder?: FolderShape | null,
 *   workspaces?: { id: string, name?: string }[],
 *   defaultWorkspaceId?: string | null,
 *   onClose: () => void,
 *   onSaved: () => void,
 * }} props
 */
export default function TeamInboxFolderModal({
  open,
  mode,
  folder = null,
  workspaces = [],
  defaultWorkspaceId = null,
  onClose,
  onSaved,
}) {
  const [name, setName] = useState("");
  const [icon, setIcon] = useState(DEFAULT_ICON);
  const [color, setColor] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const nameRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && folder) {
      setName(folder.name ?? folder.label?.split(" · ")[0]?.trim() ?? "");
      setIcon(folder.icon ?? DEFAULT_ICON);
      setColor(folder.color ?? "");
      setSortOrder(folder.sortOrder != null ? String(folder.sortOrder) : "");
      setWorkspaceId(folder.workspaceId ?? "");
    } else {
      setName("");
      setIcon(DEFAULT_ICON);
      setColor("");
      setSortOrder("");
      const fallback =
        defaultWorkspaceId && String(defaultWorkspaceId).trim()
          ? String(defaultWorkspaceId).trim()
          : workspaces.length === 1
            ? String(workspaces[0].id)
            : "";
      setWorkspaceId(fallback);
    }
    setErrorMsg("");
    const t = setTimeout(() => nameRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open, mode, folder, workspaces, defaultWorkspaceId]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, busy, onClose]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const trimmed = name.trim();
      if (!trimmed) {
        setErrorMsg("Folder name is required.");
        return;
      }
      const ws = (workspaceId || "").trim();
      if (!ws) {
        setErrorMsg("Select a workspace for this folder.");
        return;
      }
      setBusy(true);
      setErrorMsg("");
      const body = {
        name: trimmed,
        icon: (icon || DEFAULT_ICON).trim() || DEFAULT_ICON,
      };
      const c = color.trim();
      if (c) body.color = c;
      const so = sortOrder.trim();
      if (so !== "" && Number.isFinite(Number(so))) body.sort_order = Number(so);

      try {
        if (mode === "create") {
          await createTeamInbox({ workspaceId: ws, body });
        } else if (folder?.id) {
          await updateTeamInbox({ workspaceId: ws, inboxId: folder.id, body });
        }
        onSaved();
        onClose();
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : "Could not save folder.");
      } finally {
        setBusy(false);
      }
    },
    [name, icon, color, sortOrder, workspaceId, mode, folder, onSaved, onClose],
  );

  const handleArchive = useCallback(async () => {
    if (!folder?.id || !folder.workspaceId) return;
    const label = folder.label ?? folder.name ?? "this folder";
    const count = folder.count ?? 0;
    const msg =
      count > 0
        ? `Archive "${label}"? ${count} chat${count === 1 ? "" : "s"} will be moved out of this folder.`
        : `Archive "${label}"?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    setErrorMsg("");
    try {
      await archiveTeamInbox({ workspaceId: folder.workspaceId, inboxId: folder.id });
      onSaved();
      onClose();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Could not archive folder.");
    } finally {
      setBusy(false);
    }
  }, [folder, onSaved, onClose]);

  if (!open) return null;

  const showWorkspacePicker = mode === "create" && workspaces.length > 1;
  const workspaceLocked = mode === "edit";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="team-inbox-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="mx-4 w-full max-w-md rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200/60 dark:bg-slate-900 dark:ring-slate-700/60">
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5 dark:border-slate-700/60">
          <div>
            <h2 id="team-inbox-modal-title" className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {mode === "create" ? "New team inbox" : "Edit team inbox"}
            </h2>
            {mode === "edit" && folder?.workspaceName ? (
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                Workspace: <span className="font-medium text-slate-700 dark:text-slate-300">{folder.workspaceName}</span>
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="ml-4 mt-0.5 text-slate-400 transition hover:text-slate-600 disabled:opacity-50"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5" noValidate>
          {showWorkspacePicker ? (
            <div className="mb-4">
              <label htmlFor="team-inbox-ws" className="mb-1.5 block text-xs font-semibold text-slate-700 dark:text-slate-300">
                Workspace
              </label>
              <select
                id="team-inbox-ws"
                value={workspaceId}
                onChange={(e) => setWorkspaceId(e.target.value)}
                className="ui-input w-full"
                disabled={busy}
                required
              >
                <option value="">Select workspace…</option>
                {workspaces.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name || w.id}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {workspaceLocked && folder?.workspaceId ? (
            <input type="hidden" value={folder.workspaceId} readOnly />
          ) : null}

          <div className="mb-4">
            <label htmlFor="team-inbox-name" className="mb-1.5 block text-xs font-semibold text-slate-700 dark:text-slate-300">
              Name
            </label>
            <input
              ref={nameRef}
              id="team-inbox-name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errorMsg) setErrorMsg("");
              }}
              placeholder="e.g. Priority, Refunds"
              className="ui-input w-full"
              disabled={busy}
              maxLength={255}
              required
            />
          </div>

          <div className="mb-4 grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="team-inbox-icon" className="mb-1.5 block text-xs font-semibold text-slate-700 dark:text-slate-300">
                Icon class
              </label>
              <input
                id="team-inbox-icon"
                type="text"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                placeholder={DEFAULT_ICON}
                className="ui-input w-full font-mono text-xs"
                disabled={busy}
              />
            </div>
            <div>
              <label htmlFor="team-inbox-color" className="mb-1.5 block text-xs font-semibold text-slate-700 dark:text-slate-300">
                Color (optional)
              </label>
              <input
                id="team-inbox-color"
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#2563eb"
                className="ui-input w-full font-mono text-xs"
                disabled={busy}
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="team-inbox-sort" className="mb-1.5 block text-xs font-semibold text-slate-700 dark:text-slate-300">
              Sort order (optional)
            </label>
            <input
              id="team-inbox-sort"
              type="number"
              min={0}
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              placeholder="0"
              className="ui-input w-full"
              disabled={busy}
            />
          </div>

          {errorMsg ? (
            <p className="mb-3 text-xs font-medium text-rose-600 dark:text-rose-400" role="alert">
              {errorMsg}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              {mode === "edit" ? (
                <button
                  type="button"
                  onClick={() => void handleArchive()}
                  disabled={busy || !folder?.workspaceId}
                  className="rounded-lg px-3 py-2 text-xs font-semibold text-rose-700 ring-1 ring-rose-200 transition hover:bg-rose-50 disabled:opacity-50 dark:text-rose-400 dark:ring-rose-800/60 dark:hover:bg-rose-950/40"
                >
                  Archive folder
                </button>
              ) : null}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 ring-1 ring-slate-200 transition hover:bg-slate-50 disabled:opacity-50 dark:text-slate-300 dark:ring-slate-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:opacity-50"
              >
                {busy ? "Saving…" : mode === "create" ? "Create" : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
